import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  TrendingUp, 
  LogOut, 
  Menu, 
  X,
  Shield,
  AlertCircle,
  Settings,
  Key,
  Server,
  Receipt,
  Wallet,
  BarChart3,
  MessageCircle,
  ArrowDownToLine,
  Mail
} from 'lucide-react';
import Logo from '../../components/Logo';

const SecureAdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sessionTime, setSessionTime] = useState('');
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);

  useEffect(() => {
    // Check security clearance
    const clearance = sessionStorage.getItem('security_clearance');
    if (!clearance || clearance !== 'granted') {
      navigate('/secure-admin-access');
      return;
    }

    // Check if admin is authenticated
    const token = localStorage.getItem('secure_admin_token');
    if (!token) {
      navigate('/secure-admin-access/login');
      return;
    }

    // Update session timer
    const interval = setInterval(() => {
      const sessionStart = localStorage.getItem('admin_session_start');
      if (sessionStart) {
        const elapsed = Date.now() - parseInt(sessionStart);
        const minutes = Math.floor(elapsed / 60000);
        const seconds = Math.floor((elapsed % 60000) / 1000);
        setSessionTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);

        // Auto-logout after 30 minutes
        if (minutes >= 30) {
          handleLogout();
        }
      }
    }, 1000);

    // Handle resize
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate]);

  // Poll for pending withdrawals
  useEffect(() => {
    const checkPendingWithdrawals = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
        if (!token) return;

        // Use relative URL - works on any domain
        const response = await fetch('/api/withdrawal-requests/pending', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setPendingWithdrawals(data.length);
        }
      } catch (error) {
        console.error('Error checking withdrawals:', error);
      }
    };

    checkPendingWithdrawals();
    const interval = setInterval(checkPendingWithdrawals, 15000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('secure_admin_token');
    localStorage.removeItem('secure_admin_user');
    localStorage.removeItem('admin_session_start');
    sessionStorage.removeItem('security_clearance');
    sessionStorage.removeItem('clearance_timestamp');
    navigate('/secure-admin-access');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/secure-admin-access/dashboard' },
    { icon: Users, label: 'User Management', path: '/secure-admin-access/users' },
    { icon: MessageCircle, label: 'Support Messages', path: '/secure-admin-access/support-messages' },
    { icon: ArrowDownToLine, label: 'Withdrawals', path: '/secure-admin-access/withdrawals', badge: pendingWithdrawals },
    { icon: Mail, label: 'Email Campaign', path: '/secure-admin-access/email-campaign' },
    { icon: TrendingUp, label: 'Trade Analytics', path: '/secure-admin-access/trades' },
    { icon: Receipt, label: 'Transaction Control', path: '/secure-admin-access/transactions' },
    { icon: BarChart3, label: 'Trade Outcome Control', path: '/secure-admin-access/trade-control' },
    { icon: Settings, label: 'Basic Controls', path: '/secure-admin-access/controls' },
    { icon: Server, label: 'Platform Config', path: '/secure-admin-access/platform-controls' },
    { icon: Shield, label: 'KYC Management', path: '/secure-admin-access/kyc-management' },
    { icon: Wallet, label: 'Crypto Addresses', path: '/secure-admin-access/crypto-addresses' },
    { icon: Key, label: 'Passphrase Manager', path: '/secure-admin-access/passphrase' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex">
      {/* Security Status Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-green-900/20 border-b border-green-500/30 backdrop-blur-sm">
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-400" />
            <span className="text-green-400 text-xs font-semibold">SECURE SESSION ACTIVE</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-green-400 text-xs">Session: {sessionTime}</span>
            <span className="text-green-400 text-xs">|</span>
            <span className="text-green-400 text-xs">Auto-logout: {30 - Math.floor(parseInt(sessionTime.split(':')[0]))} min</span>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-40 h-screen bg-gradient-to-b from-slate-900 to-slate-800 border-r border-cyan-500/30 transition-all duration-300 mt-10 ${
          sidebarOpen ? 'w-64' : 'w-0 md:w-20'
        } overflow-hidden`}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-cyan-500/30">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center gap-2">
                <Logo size="small" showText={false} />
                <div>
                  <h1 className="text-cyan-400 font-bold text-sm">SECURE ADMIN</h1>
                  <p className="text-cyan-600 text-xs">Control Center</p>
                </div>
              </div>
            )}
            {!sidebarOpen && !isMobile && (
              <Shield className="w-6 h-6 text-cyan-400 mx-auto" />
            )}
          </div>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-2 pb-24 overflow-y-auto" style={{maxHeight: 'calc(100vh - 240px)'}}>
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all relative ${
                isActive(item.path)
                  ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/50 text-cyan-400'
                  : 'text-cyan-600 hover:bg-slate-800 hover:text-cyan-400'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-semibold">{item.label}</span>}
              {item.badge > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 w-full p-4 border-t border-cyan-500/30">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 transition-all"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-semibold">Secure Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen mt-10">
        {/* Top Header */}
        <header className="bg-slate-900/50 backdrop-blur-sm border-b border-cyan-500/30 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-cyan-400 text-sm font-semibold">Secure Admin</p>
                <p className="text-cyan-600 text-xs">Super Administrator</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <Outlet />
        </main>

        {/* Security Footer */}
        <footer className="bg-slate-900/50 border-t border-cyan-500/30 p-4">
          <div className="flex items-center justify-center gap-2 text-cyan-600 text-xs">
            <AlertCircle className="w-4 h-4" />
            <span>All activities are monitored and logged | Secure encrypted connection</span>
          </div>
        </footer>
      </div>

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 mt-10"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default SecureAdminLayout;
